import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { AlertCircle, Download, Upload, Plus, Trash2, Edit, BarChart3, Calendar, FileText, Users, MapPin, Shield, TrendingUp, FileDown, History } from "lucide-react";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Set up axios defaults
axios.defaults.headers.common['Authorization'] = '';

function App() {
  const [user, setUser] = useState(null);
  const [password, setPassword] = useState('');
  const [loginOpen, setLoginOpen] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [currentTab, setCurrentTab] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [frequentAddresses, setFrequentAddresses] = useState([]);
  const [showFrequentAddresses, setShowFrequentAddresses] = useState(false);
  const [neighborhoodSuggestion, setNeighborhoodSuggestion] = useState(null);
  const [editingIncident, setEditingIncident] = useState(null);
  const [editForm, setEditForm] = useState({});
  const fileInputRef = useRef(null);

  // Form state for adding incidents
  const [incidentForm, setIncidentForm] = useState({
    report_number: '',
    incident_date: '',
    incident_type: '',
    street_number: '',
    street_name: '',
    neighborhood: '',
    description: '',
    count: 1,
    prepared_by: '',
    district: ''
  });

  // Configuration data
  const [neighborhoods] = useState([
    "BOULEVARD", "FERNWAY", "LOMOND", "LUDLOW", "MALVERN", 
    "MERCER", "MORELAND", "ONAWAY", "SUSSEX", "LARCHMERE", 
    "FAIRMOUNT", "VAN AKEN", "CHA/LEE"
  ]);

  const [incidentTypes] = useState([
    "Homicide", "Felonious Assault", "Rape", "Arson",
    "Agg. Robbery", "Att. Robbery", "Robbery", "Agg. Burglary", "Burglary", 
    "B & E", "GTMV", "Att. GTMV", "Theft from Auto", "Theft"
  ]);

  // Analytics state
  const [monthlyData, setMonthlyData] = useState(null);
  const [neighborhoodReportData, setNeighborhoodReportData] = useState(null);
  const [threeYearData, setThreeYearData] = useState(null);
  const [neighborhoodData, setNeighborhoodData] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [numbersData, setNumbersData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedMatrixMonths, setSelectedMatrixMonths] = useState([new Date().getMonth() + 1]); // Array for multi-select
  const [showCharts, setShowCharts] = useState(false);
  const [showNeighborhoodCharts, setShowNeighborhoodCharts] = useState(false);
  const [showMatrixCharts, setShowMatrixCharts] = useState(false);
  const [showMonthlyCharts, setShowMonthlyCharts] = useState(false);
  const [showComparisonCharts, setShowComparisonCharts] = useState(false);
  const [showNumbersCharts, setShowNumbersCharts] = useState(false);
  
  // Historical data state
  const [historicalData, setHistoricalData] = useState(null);
  const [allTimeStats, setAllTimeStats] = useState(null); // Store all-time record low
  const [historicalStartYear, setHistoricalStartYear] = useState(1960);
  const [historicalEndYear, setHistoricalEndYear] = useState(new Date().getFullYear());
  const [showHistoricalCharts, setShowHistoricalCharts] = useState(false);
  const [historicalViewMode, setHistoricalViewMode] = useState('overview'); // 'overview', 'detailed', 'comparison'

  // Check for existing auth token on mount
  useEffect(() => {
    const token = localStorage.getItem('crime_dashboard_token');
    const role = localStorage.getItem('crime_dashboard_role');
    if (token && role) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser({ role });
      loadIncidents();
    }
  }, []);

  // Authentication functions
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/login`, { password });
      const { access_token, role } = response.data;
      
      localStorage.setItem('crime_dashboard_token', access_token);
      localStorage.setItem('crime_dashboard_role', role);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser({ role });
      setLoginOpen(false);
      setPassword('');
      toast.success(`Logged in as ${role}`);
      
      loadIncidents();
    } catch (error) {
      toast.error('Invalid password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('crime_dashboard_token');
    localStorage.removeItem('crime_dashboard_role');
    axios.defaults.headers.common['Authorization'] = '';
    setUser(null);
    setIncidents([]);
    toast.success('Logged out');
  };

  // Data loading functions
  const loadIncidents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/incidents`, {
        params: {
          year: selectedYear,
          month: selectedMonth
        }
      });
      setIncidents(response.data);
    } catch (error) {
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const loadFrequentAddresses = async () => {
    try {
      const response = await axios.get(`${API}/analytics/frequent-addresses`, {
        params: { year: selectedYear, min_incidents: 3 }
      });
      setFrequentAddresses(response.data.frequent_addresses);
    } catch (error) {
      console.error('Error loading frequent addresses:', error);
    }
  };

  const loadMonthlyData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/monthly-summary`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setMonthlyData(response.data);
    } catch (error) {
      toast.error('Failed to load monthly data');
    }
  };

  const loadThreeYearData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/three-year-comparison`, {
        params: { month: selectedMonth }
      });
      setThreeYearData(response.data);
    } catch (error) {
      toast.error('Failed to load three year comparison data');
    }
  };

  const loadNeighborhoodData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/neighborhood-summary`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setNeighborhoodData(response.data);
    } catch (error) {
      toast.error('Failed to load neighborhood data');
    }
  };

  const loadNeighborhoodReportData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/monthly-neighborhood-report`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setNeighborhoodReportData(response.data);
    } catch (error) {
      toast.error('Failed to load neighborhood report data');
    }
  };

  const loadMatrixData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/neighborhood-matrix`, {
        params: { 
          year: selectedYear, 
          months: selectedMatrixMonths.join(',') // Send as comma-separated string
        }
      });
      setMatrixData(response.data);
    } catch (error) {
      toast.error('Failed to load matrix data');
    }
  };

  const loadNumbersData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/numbers`, {
        params: { year: selectedYear }
      });
      setNumbersData(response.data);
    } catch (error) {
      toast.error('Failed to load numbers data');
    }
  };

  const loadHistoricalData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/historical-data`, {
        params: { 
          start_year: historicalStartYear,
          end_year: historicalEndYear
        }
      });
      setHistoricalData(response.data);
      
      // Always fetch all-time statistics from 1960 to last completed year for record low
      // Don't include current year if it's incomplete
      const currentYear = new Date().getFullYear();
      const lastCompletedYear = currentYear - 1; // Use last year as the baseline for records
      
      const allTimeResponse = await axios.get(`${API}/analytics/historical-data`, {
        params: { 
          start_year: 1960,
          end_year: currentYear // Include current year to get most recent data
        }
      });
      setAllTimeStats(allTimeResponse.data.statistics);
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load historical data');
      setLoading(false);
    }
  };

  // Form handling
  const getSuggestionForAddress = async (streetNumber, streetName) => {
    if (!streetNumber || !streetName || streetNumber.length < 2 || streetName.length < 5) {
      setNeighborhoodSuggestion(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/analytics/address-suggestions`, {
        params: { street_number: streetNumber, street_name: streetName }
      });
      setNeighborhoodSuggestion(response.data);
    } catch (error) {
      console.error('Error getting neighborhood suggestion:', error);
      setNeighborhoodSuggestion(null);
    }
  };

  const handleInputChange = (field, value) => {
    setIncidentForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Trigger neighborhood suggestion when street info changes
    if (field === 'street_number' || field === 'street_name') {
      const streetNumber = field === 'street_number' ? value : incidentForm.street_number;
      const streetName = field === 'street_name' ? value : incidentForm.street_name;
      
      // Debounce the API call
      setTimeout(() => {
        getSuggestionForAddress(streetNumber, streetName);
      }, 500);
    }
  };

  const handleAddIncident = async (e) => {
    e.preventDefault();
    if (user?.role !== 'editor') {
      toast.error('Editor access required');
      return;
    }

    try {
      await axios.post(`${API}/incidents`, incidentForm);
      toast.success('Incident added successfully');
      
      // Reset form
      setIncidentForm({
        report_number: '',
        incident_date: '',
        incident_type: '',
        street_number: '',
        street_name: '',
        neighborhood: '',
        description: '',
        count: 1,
        prepared_by: '',
        district: ''
      });
      
      loadIncidents();
      loadMonthlyData();
    } catch (error) {
      toast.error('Failed to add incident');
    }
  };

  const handleDeleteIncident = async (incidentId) => {
    if (user?.role !== 'editor') {
      toast.error('Editor access required');
      return;
    }

    try {
      await axios.delete(`${API}/incidents/${incidentId}`);
      toast.success('Incident deleted');
      loadIncidents();
      loadMonthlyData();
    } catch (error) {
      toast.error('Failed to delete incident');
    }
  };

  const handleEditIncident = (incident) => {
    setEditingIncident(incident.id);
    setEditForm({
      report_number: incident.report_number,
      incident_date: incident.incident_date,
      incident_type: incident.incident_type,
      street_number: incident.street_number,
      street_name: incident.street_name,
      count: incident.count,
      description: incident.description,
      neighborhood: incident.neighborhood
    });
  };

  const handleSaveEdit = async () => {
    try {
      const updatedIncident = {
        id: editingIncident,
        ...editForm
      };
      
      await axios.put(`${API}/incidents/${editingIncident}`, updatedIncident);
      toast.success('Incident updated successfully');
      setEditingIncident(null);
      setEditForm({});
      loadIncidents();
      loadMonthlyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update incident');
    }
  };

  const handleCancelEdit = () => {
    setEditingIncident(null);
    setEditForm({});
  };

  // CSV Import function
  const handleImportCSV = async (event) => {
    if (user?.role !== 'editor') {
      toast.error('Editor access required');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/data/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // Enhanced success message with guidance
      const message = response.data.message;
      toast.success(
        `${message}\n\n✅ TIP: Your data is spread across multiple months. Use the "Reporting Month" selector above to view data from different months (January-September 2025).`, 
        { 
          duration: 8000,  // Show for 8 seconds instead of default 3
        }
      );
      loadIncidents();
      loadMonthlyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // PDF Generation function with charts
  const generatePDF = async () => {
    try {
      toast.success('Generating PDF report with charts...');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      
      // Add header
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text('SHAKER HEIGHTS POLICE DEPARTMENT', pageWidth / 2, 20, { align: 'center' });
      pdf.text('CRIME STATISTICS REPORT', pageWidth / 2, 30, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      pdf.text(`Report Period: ${monthName}`, pageWidth / 2, 40, { align: 'center' });
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 50, { align: 'center' });
      
      let yPosition = 70;
      
      // Add monthly summary data
      if (monthlyData) {
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text('MONTHLY SUMMARY', margin, yPosition);
        yPosition += 15;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.text(`Total Incidents: ${monthlyData.total_incidents}`, margin + 5, yPosition);
        yPosition += 8;
        
        // Crime types breakdown
        pdf.text('Incidents by Type:', margin + 5, yPosition);
        yPosition += 6;
        
        Object.entries(monthlyData.by_type || {}).forEach(([type, count]) => {
          if (yPosition > pageHeight - 30) {
            pdf.addPage();
            yPosition = 30;
          }
          pdf.text(`  ${type}: ${count}`, margin + 10, yPosition);
          yPosition += 5;
        });
        
        yPosition += 10;
      }
      
      // Capture and add charts if they are currently visible
      const chartElements = document.querySelectorAll('canvas');
      if (chartElements.length > 0) {
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        
        if (yPosition > pageHeight - 60) {
          pdf.addPage();
          yPosition = 30;
        }
        
        pdf.text('VISUAL ANALYTICS', margin, yPosition);
        yPosition += 15;
        
        let chartCount = 0;
        const maxChartsPerPage = 2;
        
        for (let i = 0; i < chartElements.length && chartCount < 4; i++) {
          const canvas = chartElements[i];
          
          // Check if canvas is visible and has content
          if (canvas.offsetParent !== null && canvas.width > 0 && canvas.height > 0) {
            try {
              // Convert canvas to image
              const chartImage = canvas.toDataURL('image/png', 0.8);
              
              // Calculate dimensions to fit on page
              const chartWidth = Math.min(pageWidth - (margin * 2), 160);
              const chartHeight = (canvas.height / canvas.width) * chartWidth;
              
              // Check if we need a new page
              if (yPosition + chartHeight > pageHeight - 30) {
                pdf.addPage();
                yPosition = 30;
              }
              
              // Add chart image to PDF
              pdf.addImage(chartImage, 'PNG', margin, yPosition, chartWidth, chartHeight);
              yPosition += chartHeight + 15;
              chartCount++;
              
              // Add some spacing between charts
              if (chartCount % maxChartsPerPage === 0 && chartCount < 4) {
                pdf.addPage();
                yPosition = 30;
              }
              
            } catch (error) {
              console.warn('Could not capture chart:', error);
            }
          }
        }
      }
      
      // Add matrix data if available
      if (matrixData && yPosition < pageHeight - 50) {
        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          yPosition = 30;
        }
        
        yPosition += 10;
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text('MATRIX SUMMARY', margin, yPosition);
        yPosition += 15;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.text(`Total Violent Crimes: ${matrixData.total_violent}`, margin + 5, yPosition);
        yPosition += 6;
        pdf.text(`Total Non-Violent Crimes: ${matrixData.total_nonviolent}`, margin + 5, yPosition);
        yPosition += 6;
        pdf.text(`Grand Total: ${matrixData.total_incidents}`, margin + 5, yPosition);
      }
      
      // Add footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont(undefined, 'normal');
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        pdf.text('SHPD Crime Analytics Dashboard', margin, pageHeight - 10);
      }
      
      // Save PDF
      const fileName = `SHPD_Crime_Report_${selectedYear}_${selectedMonth.toString().padStart(2, '0')}.pdf`;
      pdf.save(fileName);
      
      toast.success('PDF report with charts generated successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF report');
    }
  };

  // Clear All Data function
  const handleClearAllData = async () => {
    if (user?.role !== 'editor') {
      toast.error('Editor access required');
      return;
    }

    try {
      const response = await axios.delete(`${API}/data/clear-all`);
      toast.success(response.data.message);
      setIncidents([]);
      setMonthlyData(null);
      setClearDialogOpen(false);
      
      // Reload all data
      loadIncidents();
      loadMonthlyData();
    } catch (error) {
      toast.error('Failed to clear data');
      setClearDialogOpen(false);
    }
  };

  // Export function
  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/data/export-csv`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      
      const csvData = response.data.data;
      const csv = convertToCSV(csvData);
      downloadCSV(csv, `crime_data_${selectedYear}_${selectedMonth}.csv`);
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const convertToCSV = (data) => {
    if (!data.length) return '';
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
    ].join('\n');
    return csvContent;
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', filename);
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Load data when tab/year/month changes
  useEffect(() => {
    if (user) {
      // Always load monthly data when month/year changes
      loadMonthlyData();
      
      // Load historical data for the monthly tab summary
      if (currentTab === 'monthly') {
        loadHistoricalData();
      }
      
      // Load tab-specific data
      if (currentTab === 'compare') {
        loadThreeYearData();
      } else if (currentTab === 'neighborhood') {
        loadNeighborhoodData();
      } else if (currentTab === 'report') {
        loadNeighborhoodReportData();
      } else if (currentTab === 'matrix') {
        loadMatrixData();
      } else if (currentTab === 'numbers') {
        loadNumbersData();
      } else if (currentTab === 'historical') {
        loadHistoricalData();
      }
    }
  }, [selectedYear, selectedMonth, user, currentTab]);

  // Auto-reload historical data when year range changes
  useEffect(() => {
    if (user && currentTab === 'historical') {
      loadHistoricalData();
    }
  }, [historicalStartYear, historicalEndYear]);

  // Additional effect to reload incidents when month changes
  useEffect(() => {
    if (user) {
      loadIncidents();
      loadMonthlyData();
      loadFrequentAddresses();
    }
  }, [selectedYear, selectedMonth, user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold">SHPD Crime Dashboard</CardTitle>
            <CardDescription>Shaker Heights Police Department</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Access Code</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter access code"
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                Access Dashboard
              </Button>
            </form>
          </CardContent>
        </Card>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white shadow-sm border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">SHPD Crime Dashboard</h1>
                <p className="text-sm text-gray-500">Shaker Heights Police Department</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge variant={user.role === 'editor' ? 'default' : 'secondary'}>
                {user.role.toUpperCase()}
              </Badge>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="monthly" className="flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>Monthly Narrative</span>
            </TabsTrigger>
            <TabsTrigger value="report" className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4" />
              <span>Monthly Report</span>
            </TabsTrigger>
            <TabsTrigger value="compare" className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4" />
              <span>3-Year Comparison</span>
            </TabsTrigger>
            <TabsTrigger value="neighborhood" className="flex items-center space-x-2">
              <MapPin className="w-4 h-4" />
              <span>By Neighborhood</span>
            </TabsTrigger>
            <TabsTrigger value="matrix" className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Neighborhood Matrix</span>
            </TabsTrigger>
            <TabsTrigger value="numbers" className="flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>Numbers</span>
            </TabsTrigger>
            <TabsTrigger value="historical" className="flex items-center space-x-2">
              <History className="w-4 h-4" />
              <span>Historical Data</span>
            </TabsTrigger>
          </TabsList>

          {/* Monthly Narrative Tab */}
          <TabsContent value="monthly" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Incident Entry</CardTitle>
                <CardDescription>Add and manage crime incidents for monthly reporting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Reporting Month - Prominent Top Section */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <Label htmlFor="monthInput" className="flex items-center gap-2 mb-2 text-base font-semibold text-blue-900">
                    <Calendar className="w-5 h-5" />
                    Reporting Month
                  </Label>
                  <Input
                    id="monthInput"
                    type="month"
                    value={`${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`}
                    onChange={(e) => {
                      const [year, month] = e.target.value.split('-');
                      setSelectedYear(parseInt(year));
                      setSelectedMonth(parseInt(month));
                      toast.success(`Switched to ${new Date(year, month - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}`);
                    }}
                    className="max-w-xs bg-white"
                  />
                </div>

                {/* Form Fields and Controls */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <Label htmlFor="preparedBy">Prepared By</Label>
                    <Input
                      id="preparedBy"
                      value={incidentForm.prepared_by}
                      onChange={(e) => handleInputChange('prepared_by', e.target.value)}
                      placeholder="Name / Unit"
                    />
                  </div>
                  <div>
                    <Label htmlFor="district">District / Focus Area</Label>
                    <Input
                      id="district"
                      value={incidentForm.district}
                      onChange={(e) => handleInputChange('district', e.target.value)}
                      placeholder="e.g., Van Aken District"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleExportCSV} variant="outline" className="w-full">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                  {user?.role === 'editor' && (
                    <div className="flex items-end">
                      <Button 
                        onClick={() => fileInputRef.current?.click()} 
                        variant="outline" 
                        className="w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Import File
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleImportCSV}
                        style={{ display: 'none' }}
                      />
                    </div>
                  )}
                  <div className="flex items-end">
                    <Button 
                      onClick={() => setShowMonthlyCharts(!showMonthlyCharts)}
                      variant={showMonthlyCharts ? "default" : "outline"}
                      className="w-full flex items-center space-x-2"
                    >
                      <BarChart3 className="w-4 h-4" />
                      <span>{showMonthlyCharts ? 'Tables' : 'Charts'}</span>
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={generatePDF} variant="outline" className="w-full flex items-center space-x-2">
                      <FileDown className="w-4 h-4" />
                      <span>PDF</span>
                    </Button>
                  </div>
                  {user?.role === 'editor' && (
                    <div className="flex items-end">
                      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="destructive" className="w-full">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Clear All
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Clear All Data</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <Alert>
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription>
                                This will permanently delete ALL crime incidents from the database. 
                                This action cannot be undone.
                              </AlertDescription>
                            </Alert>
                            <p className="text-sm text-gray-600">
                              Are you sure you want to proceed? This will clear all data across all years and neighborhoods.
                            </p>
                            <div className="flex justify-end space-x-2">
                              <Button 
                                variant="outline" 
                                onClick={() => setClearDialogOpen(false)}
                              >
                                Cancel
                              </Button>
                              <Button 
                                variant="destructive" 
                                onClick={handleClearAllData}
                              >
                                Yes, Clear All Data
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>

                {/* Historical Statistics Summary - 3 Cards with Dynamic Projection */}
                {historicalData && historicalData.statistics && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                    {/* Card 1: Lowest Total - Blue - Always shows all-time record low */}
                    <Card className="shadow-md">
                      <CardContent className="pt-6 text-center" style={{ paddingBottom: '10px' }}>
                        <div className="text-4xl font-bold text-blue-600 mb-2">
                          {allTimeStats?.lowest_year_total || historicalData.statistics.lowest_year_total || 237}
                        </div>
                        <div className="text-xs text-gray-600">
                          Lowest total recorded was <span className="font-semibold text-gray-800">{allTimeStats?.lowest_year || historicalData.statistics.lowest_year || 2021}</span>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Card 2: Current Running Total - Green */}
                    <Card className="shadow-md">
                      <CardContent className="pt-6 text-center" style={{ paddingBottom: '10px' }}>
                        {(() => {
                          const yearTotal = historicalData.yearly_totals?.[selectedYear.toString()] || 0;
                          
                          return (
                            <>
                              <div className="text-4xl font-bold text-green-600 mb-2">
                                {yearTotal.toLocaleString()}
                              </div>
                              <div className="text-xs text-gray-600">
                                Current running total for <span className="font-semibold text-gray-800">{selectedYear}</span>
                              </div>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                    
                    {/* Card 3: Projected Total - Dynamic Color Based on Trend */}
                    <Card className="shadow-md">
                      <CardContent className="pt-6 text-center" style={{ paddingBottom: '10px' }}>
                        {(() => {
                          const currentYear = new Date().getFullYear();
                          const currentMonth = new Date().getMonth() + 1;
                          
                          const yearTotal = historicalData.yearly_totals?.[selectedYear.toString()] || 0;
                          const lowest = allTimeStats?.lowest_year_total || historicalData.statistics.lowest_year_total || 237;
                          
                          // If viewing a past year, show year total instead of projection
                          if (selectedYear < currentYear) {
                            return (
                              <>
                                <div className="text-4xl font-bold text-blue-600 mb-2">
                                  {yearTotal}
                                </div>
                                <div className="text-xs text-gray-600 mb-1">
                                  Total for <span className="font-semibold text-gray-800">{selectedYear}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-2">
                                  {yearTotal < lowest ? '🎯 Record low!' : `📊 ${yearTotal - lowest} above record`}
                                </div>
                              </>
                            );
                          }
                          
                          // If viewing a future year with no data, show 0
                          if (selectedYear > currentYear) {
                            return (
                              <>
                                <div className="text-4xl font-bold text-gray-400 mb-2">
                                  0
                                </div>
                                <div className="text-xs text-gray-600 mb-1">
                                  Projected total for <span className="font-semibold text-gray-800">{selectedYear}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-2">
                                  No data yet
                                </div>
                              </>
                            );
                          }
                          
                          // For current year, calculate projection
                          const monthsElapsed = currentMonth;
                          const projected = monthsElapsed > 0 ? Math.round((yearTotal / monthsElapsed) * 12) : 0;
                          
                          // Determine color and status based on projection
                          let color = 'text-purple-600';
                          let statusText = '';
                          let statusColor = 'text-gray-600';
                          
                          if (projected < lowest) {
                            color = 'text-green-600';
                            statusText = '🎯 On track for new record low!';
                            statusColor = 'text-green-600 font-semibold';
                          } else if (projected <= lowest + 20) {
                            color = 'text-yellow-600';
                            statusText = '⚠️ Close to record low';
                            statusColor = 'text-yellow-600 font-semibold';
                          } else {
                            color = 'text-purple-600';
                            statusText = `📊 ${projected - lowest} above record`;
                            statusColor = 'text-gray-600';
                          }
                          
                          return (
                            <>
                              <div className={`text-4xl font-bold ${color} mb-2`}>
                                {projected}
                              </div>
                              <div className="text-xs text-gray-600 mb-1">
                                Projected total for <span className="font-semibold text-gray-800">{selectedYear}</span>
                              </div>
                              <div className={`text-xs ${statusColor} mt-2`}>
                                {statusText}
                              </div>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </div>
                )}


                {/* Add Incident Form */}
                {user?.role === 'editor' && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Add New Incident</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleAddIncident} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <Label htmlFor="rpt">Report #</Label>
                            <Input
                              id="rpt"
                              value={incidentForm.report_number}
                              onChange={(e) => handleInputChange('report_number', e.target.value)}
                              placeholder="25-000012"
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="date">Date</Label>
                            <Input
                              id="date"
                              type="date"
                              value={incidentForm.incident_date}
                              onChange={(e) => handleInputChange('incident_date', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="type">Incident Type</Label>
                            <Select
                              value={incidentForm.incident_type}
                              onValueChange={(value) => handleInputChange('incident_type', value)}
                              required
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {incidentTypes.map(type => (
                                  <SelectItem key={type} value={type}>{type}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="count">Count</Label>
                            <Input
                              id="count"
                              type="number"
                              min="1"
                              value={incidentForm.count}
                              onChange={(e) => handleInputChange('count', parseInt(e.target.value))}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="streetNo">Street #</Label>
                            <Input
                              id="streetNo"
                              value={incidentForm.street_number}
                              onChange={(e) => handleInputChange('street_number', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="streetName">Street Name</Label>
                            <Input
                              id="streetName"
                              value={incidentForm.street_name}
                              onChange={(e) => handleInputChange('street_name', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="ne">Neighborhood</Label>
                            {neighborhoodSuggestion && (
                              <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-blue-500" />
                                  <span className="font-medium">Suggested:</span>
                                  <button
                                    type="button"
                                    className="text-blue-600 underline"
                                    onClick={() => handleInputChange('neighborhood', neighborhoodSuggestion.historical_suggestion || neighborhoodSuggestion.suggested_neighborhood)}
                                  >
                                    {neighborhoodSuggestion.historical_suggestion || neighborhoodSuggestion.suggested_neighborhood}
                                  </button>
                                  <span className="text-gray-500">
                                    ({neighborhoodSuggestion.confidence} confidence
                                    {neighborhoodSuggestion.historical_count > 0 && `, ${neighborhoodSuggestion.historical_count} historical`})
                                  </span>
                                </div>
                              </div>
                            )}
                            <Select
                              value={incidentForm.neighborhood}
                              onValueChange={(value) => handleInputChange('neighborhood', value)}
                              required
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select neighborhood..." />
                              </SelectTrigger>
                              <SelectContent>
                                {neighborhoods.map(neighborhood => (
                                  <SelectItem key={neighborhood} value={neighborhood}>
                                    {neighborhood}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="desc">Description</Label>
                          <Textarea
                            id="desc"
                            value={incidentForm.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            required
                          />
                        </div>

                        <div className="flex space-x-4">
                          <Button type="submit" className="flex items-center space-x-2">
                            <Plus className="w-4 h-4" />
                            <span>Add Incident</span>
                          </Button>
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={() => setIncidentForm({
                              report_number: '',
                              incident_date: '',
                              incident_type: '',
                              street_number: '',
                              street_name: '',
                              neighborhood: '',
                              description: '',
                              count: 1,
                              prepared_by: '',
                              district: ''
                            })}
                          >
                            Clear Form
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                )}

                {/* Incidents Table/Charts */}
                <Card>
                  <CardHeader>
                    <CardTitle>Month Summary</CardTitle>
                    <CardDescription>
                      {monthlyData ? `${monthlyData.total_incidents} total incidents` : 'Loading...'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="text-center py-8">Loading incidents...</div>
                    ) : showMonthlyCharts ? (
                      // Chart View for Monthly Narrative
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Incidents by Type Chart */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Incidents by Type</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: Object.keys(monthlyData?.by_type || {}),
                                datasets: [{
                                  label: 'Incidents',
                                  data: Object.values(monthlyData?.by_type || {}),
                                  backgroundColor: 'rgba(16, 185, 129, 0.7)',
                                  borderColor: 'rgba(16, 185, 129, 1)',
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { display: false }
                                },
                                scales: {
                                  y: { beginAtZero: true },
                                  x: { 
                                    ticks: { 
                                      maxRotation: 45,
                                      minRotation: 45
                                    }
                                  }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>

                        {/* Incidents by Neighborhood Chart */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Incidents by Neighborhood</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Doughnut
                              data={{
                                labels: Object.keys(monthlyData?.by_neighborhood || {}),
                                datasets: [{
                                  data: Object.values(monthlyData?.by_neighborhood || {}),
                                  backgroundColor: [
                                    '#EF4444', '#F97316', '#F59E0B', '#EAB308', 
                                    '#84CC16', '#22C55E', '#10B981', '#14B8A6',
                                    '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6'
                                  ]
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { position: 'right' }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>

                        {/* Monthly Statistics */}
                        <Card className="lg:col-span-2">
                          <CardHeader>
                            <CardTitle className="text-lg">Monthly Statistics</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">{monthlyData?.total_incidents || 0}</div>
                                <div className="text-sm text-blue-500">Total Incidents</div>
                              </div>
                              <div className="text-center p-4 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">{Object.keys(monthlyData?.by_type || {}).length}</div>
                                <div className="text-sm text-green-500">Crime Types</div>
                              </div>
                              <div className="text-center p-4 bg-purple-50 rounded-lg">
                                <div className="text-2xl font-bold text-purple-600">{Object.keys(monthlyData?.by_neighborhood || {}).length}</div>
                                <div className="text-sm text-purple-500">Neighborhoods</div>
                              </div>
                              <div className="text-center p-4 bg-orange-50 rounded-lg">
                                <div className="text-2xl font-bold text-orange-600">
                                  {monthlyData?.total_incidents ? Math.round(monthlyData.total_incidents / new Date(selectedYear, selectedMonth, 0).getDate() * 10) / 10 : 0}
                                </div>
                                <div className="text-sm text-orange-500">Daily Average</div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      // Table View (existing)
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Report #</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Street #</TableHead>
                              <TableHead>Street Name</TableHead>
                              <TableHead>Count</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead>Neighborhood</TableHead>
                              {user?.role === 'editor' && <TableHead>Actions</TableHead>}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {incidents.map((incident) => (
                              <TableRow key={incident.id}>
                                <TableCell>
                                  {editingIncident === incident.id ? (
                                    <input
                                      type="text"
                                      value={editForm.report_number}
                                      onChange={(e) => setEditForm({...editForm, report_number: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm"
                                    />
                                  ) : (
                                    incident.report_number
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingIncident === incident.id ? (
                                    <input
                                      type="date"
                                      value={editForm.incident_date}
                                      onChange={(e) => setEditForm({...editForm, incident_date: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm"
                                    />
                                  ) : (
                                    incident.incident_date
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingIncident === incident.id ? (
                                    <select
                                      value={editForm.incident_type}
                                      onChange={(e) => setEditForm({...editForm, incident_type: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm"
                                    >
                                      {incidentTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    incident.incident_type
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingIncident === incident.id ? (
                                    <input
                                      type="text"
                                      value={editForm.street_number}
                                      onChange={(e) => setEditForm({...editForm, street_number: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm"
                                    />
                                  ) : (
                                    incident.street_number
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingIncident === incident.id ? (
                                    <input
                                      type="text"
                                      value={editForm.street_name}
                                      onChange={(e) => setEditForm({...editForm, street_name: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm"
                                    />
                                  ) : (
                                    incident.street_name
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingIncident === incident.id ? (
                                    <input
                                      type="number"
                                      value={editForm.count}
                                      onChange={(e) => setEditForm({...editForm, count: parseInt(e.target.value)})}
                                      className="w-full px-2 py-1 border rounded text-sm"
                                      min="1"
                                    />
                                  ) : (
                                    incident.count
                                  )}
                                </TableCell>
                                <TableCell className="max-w-xs">
                                  {editingIncident === incident.id ? (
                                    <textarea
                                      value={editForm.description}
                                      onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm"
                                      rows="2"
                                    />
                                  ) : (
                                    <div className="truncate">{incident.description}</div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingIncident === incident.id ? (
                                    <select
                                      value={editForm.neighborhood}
                                      onChange={(e) => setEditForm({...editForm, neighborhood: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm"
                                    >
                                      {neighborhoods.map(neighborhood => (
                                        <option key={neighborhood} value={neighborhood}>{neighborhood}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    incident.neighborhood
                                  )}
                                </TableCell>
                                {user?.role === 'editor' && (
                                  <TableCell>
                                    {editingIncident === incident.id ? (
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="default"
                                          onClick={handleSaveEdit}
                                        >
                                          ✓
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={handleCancelEdit}
                                        >
                                          ✕
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleEditIncident(incident)}
                                        >
                                          <Edit className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          onClick={() => handleDeleteIncident(incident.id)}
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {incidents.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            No incidents recorded for this period
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Frequent Addresses Section */}
                <Card className="mt-6">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <MapPin className="w-5 h-5" />
                          Frequent Crime Addresses ({selectedYear})
                        </CardTitle>
                        <CardDescription>
                          Addresses with 3+ incidents - {frequentAddresses.length} locations identified
                        </CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => setShowFrequentAddresses(!showFrequentAddresses)}
                      >
                        {showFrequentAddresses ? 'Hide' : 'Show'} Frequent Addresses
                      </Button>
                    </div>
                  </CardHeader>
                  {showFrequentAddresses && (
                    <CardContent>
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {frequentAddresses.map((addressData, index) => (
                          <div key={index} className="p-4 border rounded-lg">
                            <div className="font-semibold text-sm mb-2 flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-red-500" />
                              {addressData.address}
                            </div>
                            <div className="text-xs text-gray-600 mb-2">
                              <strong>{addressData.incident_count} incidents</strong> • 
                              Most common: {addressData.most_common_incident_type} ({addressData.most_common_count})
                            </div>
                            <div className="text-xs text-gray-500 mb-2">
                              Neighborhoods: {addressData.neighborhoods.join(', ')}
                            </div>
                            <div className="text-xs">
                              <strong>Incident Types:</strong>
                              <div className="mt-1">
                                {Object.entries(addressData.incident_types).map(([type, count]) => (
                                  <span key={type} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs mr-1 mb-1">
                                    {type}: {count}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {frequentAddresses.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          No addresses with 3+ incidents found for {selectedYear}
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Neighborhood Report Tab */}
          <TabsContent value="report">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>
                      {neighborhoodReportData ? 
                        `${neighborhoodReportData.month_name.toUpperCase()} ${neighborhoodReportData.year}` : 
                        'Monthly Neighborhood Report'
                      }
                    </CardTitle>
                    <CardDescription>
                      Incidents organized by neighborhood - {neighborhoodReportData?.total_incidents || 0} total incidents
                    </CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <div>
                      <Label htmlFor="reportMonth">Month/Year</Label>
                      <Input
                        id="reportMonth"
                        type="month"
                        value={`${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`}
                        onChange={(e) => {
                          const [year, month] = e.target.value.split('-');
                          setSelectedYear(parseInt(year));
                          setSelectedMonth(parseInt(month));
                        }}
                        className="w-40"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={() => setShowCharts(!showCharts)}
                        variant={showCharts ? "default" : "outline"}
                        className="flex items-center space-x-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span>{showCharts ? 'Show Tables' : 'Show Charts'}</span>
                      </Button>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={generatePDF} variant="outline" className="flex items-center space-x-2">
                        <FileDown className="w-4 h-4" />
                        <span>Generate PDF</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {neighborhoodReportData ? (
                  <div className="space-y-6">
                    {showCharts ? (
                      // Chart View
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Bar Chart */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Incidents by Neighborhood</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: Object.keys(neighborhoodReportData.neighborhood_totals),
                                datasets: [{
                                  label: 'Incidents',
                                  data: Object.values(neighborhoodReportData.neighborhood_totals),
                                  backgroundColor: 'rgba(59, 130, 246, 0.7)',
                                  borderColor: 'rgba(59, 130, 246, 1)',
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { display: false }
                                },
                                scales: {
                                  y: { beginAtZero: true }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>

                        {/* Doughnut Chart */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Distribution</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Doughnut
                              data={{
                                labels: Object.keys(neighborhoodReportData.neighborhood_totals),
                                datasets: [{
                                  data: Object.values(neighborhoodReportData.neighborhood_totals),
                                  backgroundColor: [
                                    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
                                    '#8B5CF6', '#EC4899', '#6B7280', '#14B8A6',
                                    '#F97316', '#84CC16', '#06B6D4', '#8B5CF6', '#F43F5E'
                                  ]
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { position: 'right' }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      // Table View - Neighborhood Report Format
                      <div className="space-y-8">
                        {Object.entries(neighborhoodReportData.neighborhood_incidents).map(([neighborhood, incidents]) => (
                          <div key={neighborhood} className="border rounded-lg overflow-hidden">
                            {/* Neighborhood Header */}
                            <div className="bg-gray-900 text-white px-4 py-2">
                              <h3 className="font-bold text-lg">
                                {neighborhood === 'CHA/LEE' ? 'CHAGRIN/LEE BUSINESS DISTRICT' : neighborhood}
                              </h3>
                            </div>
                            
                            {/* Incidents Table */}
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-gray-50">
                                    <TableHead className="font-semibold">Rpt #</TableHead>
                                    <TableHead className="font-semibold">Date</TableHead>
                                    <TableHead className="font-semibold">Inc.Type</TableHead>
                                    <TableHead className="font-semibold">Street #</TableHead>
                                    <TableHead className="font-semibold">Street Name</TableHead>
                                    <TableHead className="font-semibold">Description</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {incidents.map((incident, index) => (
                                    <TableRow key={index}>
                                      <TableCell className="font-mono text-sm">
                                        {incident.report_number}
                                      </TableCell>
                                      <TableCell className="font-mono text-sm">
                                        {incident.incident_date}
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {incident.incident_type}
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {incident.street_number}
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {incident.street_name}
                                      </TableCell>
                                      <TableCell className="text-sm max-w-xs">
                                        {incident.description}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {/* Total Row */}
                                  <TableRow className="bg-yellow-50 border-t-2 border-yellow-300">
                                    <TableCell colSpan={5} className="font-bold text-right">
                                      TOTAL:
                                    </TableCell>
                                    <TableCell className="font-bold">
                                      {neighborhoodReportData.neighborhood_totals[neighborhood]}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        ))}

                        {/* Grand Total */}
                        <Card className="bg-blue-50 border-blue-200">
                          <CardContent className="pt-6">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-blue-700">
                                GRAND TOTAL: {neighborhoodReportData.total_incidents}
                              </div>
                              <div className="text-sm text-blue-600 mt-1">
                                Total incidents across all neighborhoods for {neighborhoodReportData.month_name} {neighborhoodReportData.year}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">Loading neighborhood report...</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 3-Year Comparison Tab */}
          <TabsContent value="compare">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>3-Year Comparison - {new Date(2000, selectedMonth - 1).toLocaleString('en-US', { month: 'long' })}</CardTitle>
                    <CardDescription>Compare {new Date(2000, selectedMonth - 1).toLocaleString('en-US', { month: 'long' })} data across the last 3 years</CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <div>
                      <Label htmlFor="monthSelectComparison">Month</Label>
                      <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">January</SelectItem>
                          <SelectItem value="2">February</SelectItem>
                          <SelectItem value="3">March</SelectItem>
                          <SelectItem value="4">April</SelectItem>
                          <SelectItem value="5">May</SelectItem>
                          <SelectItem value="6">June</SelectItem>
                          <SelectItem value="7">July</SelectItem>
                          <SelectItem value="8">August</SelectItem>
                          <SelectItem value="9">September</SelectItem>
                          <SelectItem value="10">October</SelectItem>
                          <SelectItem value="11">November</SelectItem>
                          <SelectItem value="12">December</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={() => setShowComparisonCharts(!showComparisonCharts)}
                        variant={showComparisonCharts ? "default" : "outline"}
                        className="flex items-center space-x-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span>{showComparisonCharts ? 'Show Tables' : 'Show Charts'}</span>
                      </Button>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={generatePDF} variant="outline" className="flex items-center space-x-2">
                        <FileDown className="w-4 h-4" />
                        <span>Generate PDF</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {threeYearData ? (
                  <div className="space-y-6">
                    {showComparisonCharts ? (
                      // Chart View
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Years Comparison Chart */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Total Incidents by Year</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: threeYearData.years || [],
                                datasets: [{
                                  label: 'Total Incidents',
                                  data: (threeYearData.years || []).map(year => threeYearData.totals[year] || 0),
                                  backgroundColor: 'rgba(59, 130, 246, 0.7)',
                                  borderColor: 'rgba(59, 130, 246, 1)',
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { display: false }
                                },
                                scales: {
                                  y: { beginAtZero: true }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>

                        {/* Top Crime Types Across Years */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Crime Types Across Years</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: incidentTypes,
                                datasets: (threeYearData.years || []).map((year, index) => ({
                                  label: year.toString(),
                                  data: incidentTypes.map(type => threeYearData.by_type[type]?.[year] || 0),
                                  backgroundColor: `rgba(${index === 0 ? '239, 68, 68' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.7)`,
                                  borderColor: `rgba(${index === 0 ? '220, 38, 38' : index === 1 ? '37, 99, 235' : '5, 150, 105'}, 1)`,
                                  borderWidth: 1
                                }))
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { position: 'top' }
                                },
                                scales: {
                                  y: { beginAtZero: true },
                                  x: { 
                                    ticks: { 
                                      maxRotation: 45,
                                      minRotation: 45
                                    }
                                  }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>

                        {/* Monthly Trends */}
                        <Card className="lg:col-span-2">
                          <CardHeader>
                            <CardTitle className="text-lg">Monthly Trends Across Years</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                                datasets: (threeYearData.years || []).map((year, index) => ({
                                  label: year.toString(),
                                  data: Array.from({length: 12}, (_, i) => threeYearData.by_month[i + 1]?.[year] || 0),
                                  backgroundColor: `rgba(${index === 0 ? '239, 68, 68' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.7)`,
                                  borderColor: `rgba(${index === 0 ? '220, 38, 38' : index === 1 ? '37, 99, 235' : '5, 150, 105'}, 1)`,
                                  borderWidth: 1
                                }))
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { position: 'top' }
                                },
                                scales: {
                                  y: { beginAtZero: true }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      // Table View (existing)
                      <div className="space-y-6">
                        {/* By Type Table */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">By Type</h3>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead></TableHead>
                                  {incidentTypes.map(type => (
                                    <TableHead key={type} className="text-xs">{type}</TableHead>
                                  ))}
                                  <TableHead className="font-bold">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {threeYearData.years.map(year => (
                                  <TableRow key={year}>
                                    <TableCell className="font-semibold">{year}</TableCell>
                                    {incidentTypes.map(type => (
                                      <TableCell key={type}>
                                        {threeYearData.by_type[type]?.[year] || 0}
                                      </TableCell>
                                    ))}
                                    <TableCell className="font-bold">
                                      {threeYearData.totals[year] || 0}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {threeYearData.type_changes && (
                                  <TableRow className="bg-red-50">
                                    <TableCell className="font-semibold">% Change</TableCell>
                                    {incidentTypes.map(type => (
                                      <TableCell key={type} className={
                                        threeYearData.type_changes[type] > 0 ? "text-red-600" : 
                                        threeYearData.type_changes[type] < 0 ? "text-green-600" : ""
                                      }>
                                        {threeYearData.type_changes[type] > 0 ? '+' : ''}
                                        {threeYearData.type_changes[type] || 0}%
                                      </TableCell>
                                    ))}
                                    <TableCell></TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        </div>

                        {/* By Month Table */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">By Month</h3>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead></TableHead>
                                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => (
                                    <TableHead key={month}>{month}</TableHead>
                                  ))}
                                  <TableHead className="font-bold">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {threeYearData.years.map(year => (
                                  <TableRow key={year}>
                                    <TableCell className="font-semibold">{year}</TableCell>
                                    {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                                      <TableCell key={month}>
                                        {threeYearData.by_month[month]?.[year] || 0}
                                      </TableCell>
                                    ))}
                                    <TableCell className="font-bold">
                                      {threeYearData.totals[year] || 0}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {threeYearData.month_changes && (
                                  <TableRow className="bg-red-50">
                                    <TableCell className="font-semibold">% Change</TableCell>
                                    {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                                      <TableCell key={month} className={
                                        threeYearData.month_changes[month] > 0 ? "text-red-600" : 
                                        threeYearData.month_changes[month] < 0 ? "text-green-600" : ""
                                      }>
                                        {threeYearData.month_changes[month] > 0 ? '+' : ''}
                                        {threeYearData.month_changes[month] || 0}%
                                      </TableCell>
                                    ))}
                                    <TableCell></TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">Loading comparison data...</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Summary by Neighborhood Tab */}
          <TabsContent value="neighborhood">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Summary by Neighborhood - {new Date(selectedYear, selectedMonth - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })}</CardTitle>
                    <CardDescription>Neighborhood analysis for {new Date(selectedYear, selectedMonth - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })}</CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <div>
                      <Label htmlFor="monthSelectNeighborhood">Month</Label>
                      <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">January</SelectItem>
                          <SelectItem value="2">February</SelectItem>
                          <SelectItem value="3">March</SelectItem>
                          <SelectItem value="4">April</SelectItem>
                          <SelectItem value="5">May</SelectItem>
                          <SelectItem value="6">June</SelectItem>
                          <SelectItem value="7">July</SelectItem>
                          <SelectItem value="8">August</SelectItem>
                          <SelectItem value="9">September</SelectItem>
                          <SelectItem value="10">October</SelectItem>
                          <SelectItem value="11">November</SelectItem>
                          <SelectItem value="12">December</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="yearSelect">Year</Label>
                      <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Generate years from 2021 to current year + 1 */}
                          {Array.from({ length: new Date().getFullYear() - 2020 + 1 }, (_, i) => 2021 + i).reverse().map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={() => setShowNeighborhoodCharts(!showNeighborhoodCharts)}
                        variant={showNeighborhoodCharts ? "default" : "outline"}
                        className="flex items-center space-x-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span>{showNeighborhoodCharts ? 'Show Tables' : 'Show Charts'}</span>
                      </Button>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={generatePDF} variant="outline" className="flex items-center space-x-2">
                        <FileDown className="w-4 h-4" />
                        <span>Generate PDF</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {neighborhoodData ? (
                  <div className="space-y-6">
                    {showNeighborhoodCharts ? (
                      // Chart View
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Quarterly Chart */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Quarterly Totals</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: ['Q1', 'Q2', 'Q3', 'Q4'],
                                datasets: [{
                                  label: 'Incidents',
                                  data: [
                                    neighborhoodData.quarterly_totals?.Q1 || 0,
                                    neighborhoodData.quarterly_totals?.Q2 || 0,
                                    neighborhoodData.quarterly_totals?.Q3 || 0,
                                    neighborhoodData.quarterly_totals?.Q4 || 0
                                  ],
                                  backgroundColor: 'rgba(34, 197, 94, 0.7)',
                                  borderColor: 'rgba(34, 197, 94, 1)',
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { display: false }
                                },
                                scales: {
                                  y: { beginAtZero: true }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>

                        {/* Crime Types Distribution */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Crime Types Distribution</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Doughnut
                              data={{
                                labels: Object.keys(neighborhoodData.yearly_totals_by_type || {}),
                                datasets: [{
                                  data: Object.values(neighborhoodData.yearly_totals_by_type || {}),
                                  backgroundColor: [
                                    '#EF4444', '#F97316', '#F59E0B', '#EAB308', 
                                    '#84CC16', '#22C55E', '#10B981', '#14B8A6',
                                    '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6'
                                  ]
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { position: 'right' }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>

                        {/* Top Neighborhoods Chart */}
                        <Card className="lg:col-span-2">
                          <CardHeader>
                            <CardTitle className="text-lg">Top Neighborhoods by Incidents</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: neighborhoods,
                                datasets: [{
                                  label: 'Total Incidents',
                                  data: neighborhoods.map(n => {
                                    const nData = neighborhoodData.quarterly_by_neighborhood[n];
                                    return (nData?.Q1 || 0) + (nData?.Q2 || 0) + (nData?.Q3 || 0) + (nData?.Q4 || 0);
                                  }),
                                  backgroundColor: 'rgba(168, 85, 247, 0.7)',
                                  borderColor: 'rgba(168, 85, 247, 1)',
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { display: false }
                                },
                                scales: {
                                  y: { beginAtZero: true },
                                  x: { 
                                    ticks: { 
                                      maxRotation: 45,
                                      minRotation: 45
                                    }
                                  }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      // Table View (existing)
                      <div className="space-y-6">
                        {/* Quarterly Totals */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Quarterly Totals by Neighborhood / Districts</h3>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead></TableHead>
                                  {neighborhoods.map(neighborhood => (
                                    <TableHead key={neighborhood} className="text-xs">
                                      {neighborhood.length > 6 ? neighborhood.slice(0, 6) : neighborhood}
                                    </TableHead>
                                  ))}
                                  <TableHead className="font-bold">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {['Q1', 'Q2', 'Q3', 'Q4'].map(quarter => (
                                  <TableRow key={quarter}>
                                    <TableCell className="font-semibold">{quarter}</TableCell>
                                    {neighborhoods.map(neighborhood => (
                                      <TableCell key={neighborhood}>
                                        {neighborhoodData.quarterly_by_neighborhood[neighborhood]?.[quarter] || 0}
                                      </TableCell>
                                    ))}
                                    <TableCell className="font-bold">
                                      {neighborhoodData.quarterly_totals[quarter] || 0}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>

                        {/* City Totals by Type */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Totals Across City (Selected Year)</h3>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead></TableHead>
                                  {incidentTypes.map(type => (
                                    <TableHead key={type} className="text-xs">{type}</TableHead>
                                  ))}
                                  <TableHead className="font-bold">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                <TableRow>
                                  <TableCell className="font-semibold">Total by Type</TableCell>
                                  {incidentTypes.map(type => (
                                    <TableCell key={type}>
                                      {neighborhoodData.yearly_totals_by_type[type] || 0}
                                    </TableCell>
                                  ))}
                                  <TableCell className="font-bold">
                                    {Object.values(neighborhoodData.yearly_totals_by_type).reduce((sum, val) => sum + val, 0)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">Loading neighborhood data...</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Neighborhood Matrix Tab */}
          <TabsContent value="matrix">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>
                      Neighborhood Matrix - {selectedMatrixMonths.length === 12 ? `${selectedYear} (Full Year)` : 
                        selectedMatrixMonths.length === 1 ? 
                          new Date(selectedYear, selectedMatrixMonths[0] - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' }) :
                          `${selectedYear} (${selectedMatrixMonths.length} months selected)`
                      }
                    </CardTitle>
                    <CardDescription>
                      Crime type × neighborhood matrix for selected period
                    </CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <div className="relative">
                      <Label htmlFor="monthSelectMatrix">Months (click to toggle)</Label>
                      <div className="border rounded-md p-2 w-64 max-h-32 overflow-y-auto bg-white">
                        <div className="flex flex-wrap gap-1">
                          {[
                            { value: 1, label: 'Jan' },
                            { value: 2, label: 'Feb' },
                            { value: 3, label: 'Mar' },
                            { value: 4, label: 'Apr' },
                            { value: 5, label: 'May' },
                            { value: 6, label: 'Jun' },
                            { value: 7, label: 'Jul' },
                            { value: 8, label: 'Aug' },
                            { value: 9, label: 'Sep' },
                            { value: 10, label: 'Oct' },
                            { value: 11, label: 'Nov' },
                            { value: 12, label: 'Dec' }
                          ].map(month => {
                            const isSelected = selectedMatrixMonths.includes(month.value);
                            return (
                              <button
                                key={month.value}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    // Remove month if already selected (but keep at least one)
                                    if (selectedMatrixMonths.length > 1) {
                                      setSelectedMatrixMonths(selectedMatrixMonths.filter(m => m !== month.value));
                                    }
                                  } else {
                                    // Add month
                                    setSelectedMatrixMonths([...selectedMatrixMonths, month.value].sort((a, b) => a - b));
                                  }
                                }}
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                  isSelected 
                                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                {month.label}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-2 pt-2 border-t flex gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedMatrixMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Select All
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedMatrixMonths([new Date().getMonth() + 1])}
                            className="text-xs text-gray-600 hover:text-gray-800"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="yearSelect2">Year</Label>
                      <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Generate years from 2021 to current year + 1 */}
                          {Array.from({ length: new Date().getFullYear() - 2020 + 1 }, (_, i) => 2021 + i).reverse().map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={() => setShowMatrixCharts(!showMatrixCharts)}
                        variant={showMatrixCharts ? "default" : "outline"}
                        className="flex items-center space-x-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span>{showMatrixCharts ? 'Show Matrix' : 'Show Charts'}</span>
                      </Button>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={generatePDF} variant="outline" className="flex items-center space-x-2">
                        <FileDown className="w-4 h-4" />
                        <span>Generate PDF</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {matrixData ? (
                  <div className="space-y-6">
                    {showMatrixCharts ? (
                      // Chart View
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Violent vs Non-Violent Chart */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Violent vs Non-Violent Crimes</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Doughnut
                              data={{
                                labels: ['Violent Crimes', 'Non-Violent Crimes'],
                                datasets: [{
                                  data: [matrixData.total_violent, matrixData.total_nonviolent],
                                  backgroundColor: ['#EF4444', '#3B82F6'],
                                  borderColor: ['#DC2626', '#2563EB'],
                                  borderWidth: 2
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { position: 'bottom' }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>

                        {/* Top Crime Types */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Crime Types Overview</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: Object.keys(matrixData.crime_type_totals || {}),
                                datasets: [{
                                  label: 'Incidents',
                                  data: Object.values(matrixData.crime_type_totals || {}),
                                  backgroundColor: (ctx) => {
                                    const label = ctx.parsed ? Object.keys(matrixData.crime_type_totals || {})[ctx.dataIndex] : '';
                                    return matrixData.violent_crimes?.includes(label) ? 'rgba(239, 68, 68, 0.7)' : 'rgba(59, 130, 246, 0.7)';
                                  },
                                  borderColor: (ctx) => {
                                    const label = ctx.parsed ? Object.keys(matrixData.crime_type_totals || {})[ctx.dataIndex] : '';
                                    return matrixData.violent_crimes?.includes(label) ? 'rgba(220, 38, 38, 1)' : 'rgba(37, 99, 235, 1)';
                                  },
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { display: false }
                                },
                                scales: {
                                  y: { beginAtZero: true },
                                  x: { 
                                    ticks: { 
                                      maxRotation: 45,
                                      minRotation: 45
                                    }
                                  }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>

                        {/* Neighborhood Comparison */}
                        <Card className="lg:col-span-2">
                          <CardHeader>
                            <CardTitle className="text-lg">Neighborhood Crime Distribution</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: neighborhoods,
                                datasets: [
                                  {
                                    label: 'Violent Crimes',
                                    data: neighborhoods.map(n => matrixData.violent_totals[n] || 0),
                                    backgroundColor: 'rgba(239, 68, 68, 0.7)',
                                    borderColor: 'rgba(220, 38, 38, 1)',
                                    borderWidth: 1
                                  },
                                  {
                                    label: 'Non-Violent Crimes',
                                    data: neighborhoods.map(n => matrixData.nonviolent_totals[n] || 0),
                                    backgroundColor: 'rgba(59, 130, 246, 0.7)',
                                    borderColor: 'rgba(37, 99, 235, 1)',
                                    borderWidth: 1
                                  }
                                ]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { position: 'top' }
                                },
                                scales: {
                                  y: { 
                                    beginAtZero: true,
                                    stacked: true
                                  },
                                  x: { 
                                    stacked: true,
                                    ticks: { 
                                      maxRotation: 45,
                                      minRotation: 45
                                    }
                                  }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      // Matrix Table View (existing)
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead></TableHead>
                              {neighborhoods.map(neighborhood => (
                                <TableHead key={neighborhood} className="text-xs">
                                  {neighborhood.length > 6 ? neighborhood.slice(0, 6) : neighborhood}
                                </TableHead>
                              ))}
                              <TableHead className="font-bold bg-yellow-100">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {/* Non-violent crimes first */}
                            {matrixData.nonviolent_crimes.map(crimeType => (
                              <TableRow key={crimeType}>
                                <TableCell className="font-semibold">{crimeType}</TableCell>
                                {neighborhoods.map(neighborhood => (
                                  <TableCell key={neighborhood}>
                                    {matrixData.matrix[crimeType]?.[neighborhood] || 0}
                                  </TableCell>
                                ))}
                                <TableCell className="font-bold bg-yellow-100">
                                  {matrixData.crime_type_totals[crimeType] || 0}
                                </TableCell>
                              </TableRow>
                            ))}
                            
                            {/* Separator row */}
                            <TableRow className="border-t-2 border-gray-400">
                              <TableCell colSpan={neighborhoods.length + 2}></TableCell>
                            </TableRow>

                            {/* Violent crimes */}
                            {matrixData.violent_crimes.map(crimeType => (
                              <TableRow key={crimeType} className="bg-red-50">
                                <TableCell className="font-semibold">{crimeType}</TableCell>
                                {neighborhoods.map(neighborhood => (
                                  <TableCell key={neighborhood}>
                                    {matrixData.matrix[crimeType]?.[neighborhood] || 0}
                                  </TableCell>
                                ))}
                                <TableCell className="font-bold bg-yellow-100">
                                  {matrixData.crime_type_totals[crimeType] || 0}
                                </TableCell>
                              </TableRow>
                            ))}

                            {/* Total rows */}
                            <TableRow className="border-t-2 border-gray-600 bg-gray-100">
                              <TableCell className="font-bold">Total</TableCell>
                              {neighborhoods.map(neighborhood => (
                                <TableCell key={neighborhood} className="font-bold">
                                  {matrixData.neighborhood_totals[neighborhood] || 0}
                                </TableCell>
                              ))}
                              <TableCell className="font-bold bg-yellow-100">
                                {matrixData.total_incidents}
                              </TableCell>
                            </TableRow>
                            <TableRow className="bg-red-100">
                              <TableCell className="font-bold">Violent</TableCell>
                              {neighborhoods.map(neighborhood => (
                                <TableCell key={neighborhood} className="font-bold">
                                  {matrixData.violent_totals[neighborhood] || 0}
                                </TableCell>
                              ))}
                              <TableCell className="font-bold bg-yellow-100">
                                {matrixData.total_violent}
                              </TableCell>
                            </TableRow>
                            <TableRow className="bg-orange-100">
                              <TableCell className="font-bold">Non-Violent</TableCell>
                              {neighborhoods.map(neighborhood => (
                                <TableCell key={neighborhood} className="font-bold">
                                  {matrixData.nonviolent_totals[neighborhood] || 0}
                                </TableCell>
                              ))}
                              <TableCell className="font-bold bg-yellow-100">
                                {matrixData.total_nonviolent}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">Loading matrix data...</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Numbers Tab */}
          <TabsContent value="numbers">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>{new Date(selectedYear, selectedMonth - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })} Numbers</CardTitle>
                    <CardDescription>Crime statistics for {new Date(selectedYear, selectedMonth - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })}</CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <div>
                      <Label htmlFor="monthSelect3">Month</Label>
                      <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">January</SelectItem>
                          <SelectItem value="2">February</SelectItem>
                          <SelectItem value="3">March</SelectItem>
                          <SelectItem value="4">April</SelectItem>
                          <SelectItem value="5">May</SelectItem>
                          <SelectItem value="6">June</SelectItem>
                          <SelectItem value="7">July</SelectItem>
                          <SelectItem value="8">August</SelectItem>
                          <SelectItem value="9">September</SelectItem>
                          <SelectItem value="10">October</SelectItem>
                          <SelectItem value="11">November</SelectItem>
                          <SelectItem value="12">December</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="yearSelect3">Year</Label>
                      <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Generate years from 2021 to current year + 1 */}
                          {Array.from({ length: new Date().getFullYear() - 2020 + 1 }, (_, i) => 2021 + i).reverse().map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={() => setShowNumbersCharts(!showNumbersCharts)}
                        variant={showNumbersCharts ? "default" : "outline"}
                        className="flex items-center space-x-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span>{showNumbersCharts ? 'Show Table' : 'Show Charts'}</span>
                      </Button>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={generatePDF} variant="outline" className="flex items-center space-x-2">
                        <FileDown className="w-4 h-4" />
                        <span>Generate PDF</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {numbersData ? (
                  <div className="space-y-6">
                    {showNumbersCharts ? (
                      // Chart View - Show only selected month
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Selected Month Crime Types */}
                        <Card className="lg:col-span-2">
                          <CardHeader>
                            <CardTitle className="text-lg">Crime Types for {new Date(selectedYear, selectedMonth - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Bar
                              data={{
                                labels: incidentTypes,
                                datasets: [{
                                  label: 'Incidents',
                                  data: incidentTypes.map(type => numbersData.monthly_data[selectedMonth]?.[type] || 0),
                                  backgroundColor: 'rgba(59, 130, 246, 0.7)',
                                  borderColor: 'rgba(59, 130, 246, 1)',
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: { display: false }
                                },
                                scales: {
                                  y: { beginAtZero: true }
                                }
                              }}
                            />
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      // Table View - Show only selected month
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Crime Type</TableHead>
                              <TableHead className="text-center">Count</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {incidentTypes.map(type => {
                              const count = numbersData.monthly_data[selectedMonth]?.[type] || 0;
                              return (
                                <TableRow key={type}>
                                  <TableCell className="font-semibold">{type}</TableCell>
                                  <TableCell className="text-center">{count}</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow className="bg-gray-100 border-t-2 border-gray-600">
                              <TableCell className="font-bold">Total</TableCell>
                              <TableCell className="text-center font-bold">
                                {Object.values(numbersData.monthly_data[selectedMonth] || {}).reduce((sum, val) => sum + val, 0)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">Loading numbers data...</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Historical Data Tab */}
          <TabsContent value="historical" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Historical Crime Data (1960 - Present)</CardTitle>
                <CardDescription>
                  View and analyze crime trends across decades
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Year Range Selection */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="startYear">Start Year</Label>
                    <Input
                      id="startYear"
                      type="number"
                      min="1960"
                      max={historicalEndYear}
                      value={historicalStartYear}
                      onChange={(e) => setHistoricalStartYear(parseInt(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="endYear">End Year</Label>
                    <Input
                      id="endYear"
                      type="number"
                      min={historicalStartYear}
                      max={new Date().getFullYear()}
                      value={historicalEndYear}
                      onChange={(e) => setHistoricalEndYear(parseInt(e.target.value))}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={loadHistoricalData} className="w-full">
                      Load Data
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={() => setShowHistoricalCharts(!showHistoricalCharts)} 
                      variant="outline"
                      className="w-full"
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      {showHistoricalCharts ? 'Hide' : 'Show'} Charts
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading historical data...</p>
                  </div>
                ) : historicalData ? (
                  <div className="space-y-6">
                    {/* Statistics Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-sm text-gray-500">Total Years</p>
                            <p className="text-3xl font-bold text-blue-600">
                              {historicalData.statistics.total_years}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-sm text-gray-500">Total Incidents</p>
                            <p className="text-3xl font-bold text-purple-600">
                              {historicalData.statistics.total_incidents.toLocaleString()}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-sm text-gray-500">Peak Year</p>
                            <p className="text-3xl font-bold text-red-600">
                              {historicalData.statistics.peak_year}
                            </p>
                            <p className="text-sm text-gray-500">
                              {historicalData.statistics.peak_year_total} incidents
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-sm text-gray-500">Average/Year</p>
                            <p className="text-3xl font-bold text-green-600">
                              {historicalData.statistics.average_per_year}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* View Mode Selection */}
                    <div className="flex space-x-2">
                      <Button 
                        variant={historicalViewMode === 'overview' ? 'default' : 'outline'}
                        onClick={() => setHistoricalViewMode('overview')}
                      >
                        Overview
                      </Button>
                      <Button 
                        variant={historicalViewMode === 'detailed' ? 'default' : 'outline'}
                        onClick={() => setHistoricalViewMode('detailed')}
                      >
                        Year by Year
                      </Button>
                      <Button 
                        variant={historicalViewMode === 'decades' ? 'default' : 'outline'}
                        onClick={() => setHistoricalViewMode('decades')}
                      >
                        By Decade
                      </Button>
                    </div>

                    {/* Overview Mode - Table Like Numbers Tab with All Crime Types */}
                    {historicalViewMode === 'overview' && (
                      <div className="space-y-6">
                        {/* Yearly Table with All Crime Type Columns */}
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="font-bold sticky left-0 bg-white">Year</TableHead>
                                <TableHead className="text-center font-bold">Theft from Auto</TableHead>
                                <TableHead className="text-center font-bold">Burglary</TableHead>
                                <TableHead className="text-center font-bold">Agg. Burglary</TableHead>
                                <TableHead className="text-center font-bold">Att. GTMV</TableHead>
                                <TableHead className="text-center font-bold">GTMV</TableHead>
                                <TableHead className="text-center font-bold">Theft</TableHead>
                                <TableHead className="text-center font-bold">B&E</TableHead>
                                <TableHead className="text-center font-bold">Assault</TableHead>
                                <TableHead className="text-center font-bold">Arson</TableHead>
                                <TableHead className="text-center font-bold">Fel. Assault</TableHead>
                                <TableHead className="text-center font-bold">Agg. Robbery</TableHead>
                                <TableHead className="text-center font-bold">Att. Robbery</TableHead>
                                <TableHead className="text-center font-bold">Robbery</TableHead>
                                <TableHead className="text-center font-bold">Homicide</TableHead>
                                <TableHead className="text-center font-bold">Rape</TableHead>
                                <TableHead className="text-center font-bold bg-gray-100">Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {Object.entries(historicalData.yearly_data || {})
                                .filter(([year, data]) => data.total > 0)
                                .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
                                .map(([year, data]) => {
                                  const breakdown = data.crime_breakdown || {};
                                  return (
                                    <TableRow key={year}>
                                      <TableCell className="font-semibold sticky left-0 bg-white">{year}</TableCell>
                                      <TableCell className="text-center">{breakdown["Theft from Auto"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Burglary"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Agg. Burglary"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Att. GTMV"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["GTMV"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Theft"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["B&E"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Assault"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Arson"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Fel. Assault"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Agg. Robbery"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Att. Robbery"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Robbery"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Homicide"] || 0}</TableCell>
                                      <TableCell className="text-center">{breakdown["Rape"] || 0}</TableCell>
                                      <TableCell className="text-center font-bold bg-gray-50">{data.total.toLocaleString()}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              {/* Grand Total Row */}
                              <TableRow className="bg-gray-100 border-t-2 border-gray-600 font-bold">
                                <TableCell className="font-bold sticky left-0 bg-gray-100">Total ({historicalStartYear}-{historicalEndYear})</TableCell>
                                {historicalData.crime_types && historicalData.crime_types.map(crimeType => {
                                  const total = Object.values(historicalData.yearly_data || {})
                                    .reduce((sum, data) => sum + (data.crime_breakdown?.[crimeType] || 0), 0);
                                  return (
                                    <TableCell key={crimeType} className="text-center font-bold">
                                      {total.toLocaleString()}
                                    </TableCell>
                                  );
                                })}
                                <TableCell className="text-center font-bold bg-gray-200">
                                  {Object.values(historicalData.yearly_totals || {})
                                    .reduce((sum, val) => sum + val, 0)
                                    .toLocaleString()}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>

                        {showHistoricalCharts && (
                          <div className="space-y-6">
                            {/* Total Crimes by Year Chart */}
                            <Card>
                              <CardHeader>
                                <CardTitle>Total Crimes by Year</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div style={{ height: '400px' }}>
                                  {historicalData.yearly_totals && Object.keys(historicalData.yearly_totals).length > 0 ? (
                                    <Bar
                                      data={{
                                        labels: Object.keys(historicalData.yearly_totals).filter(y => historicalData.yearly_totals[y] > 0),
                                        datasets: [
                                          {
                                            label: 'Total Incidents',
                                            data: Object.entries(historicalData.yearly_totals)
                                              .filter(([y, t]) => t > 0)
                                              .map(([y, t]) => t),
                                            backgroundColor: 'rgba(59, 130, 246, 0.7)',
                                            borderColor: 'rgba(59, 130, 246, 1)',
                                            borderWidth: 2
                                          }
                                        ]
                                      }}
                                      options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        scales: {
                                          y: {
                                            beginAtZero: true,
                                            title: {
                                              display: true,
                                              text: 'Number of Incidents'
                                            }
                                          },
                                          x: {
                                            title: {
                                              display: true,
                                              text: 'Year'
                                            }
                                          }
                                        },
                                        plugins: {
                                          legend: {
                                            display: true,
                                            position: 'top'
                                          },
                                          title: {
                                            display: true,
                                            text: `Total Crime Incidents by Year (${historicalStartYear} - ${historicalEndYear})`
                                          }
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div className="flex items-center justify-center h-full text-gray-500">
                                      No data available for chart
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>

                            {/* Crime Types Breakdown Chart */}
                            <Card>
                              <CardHeader>
                                <CardTitle>Crime Types Over Time (Stacked)</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div style={{ height: '500px' }}>
                                  {historicalData.crime_types && historicalData.yearly_data ? (
                                    <Bar
                                      data={{
                                        labels: Object.keys(historicalData.yearly_totals || {}).filter(y => historicalData.yearly_totals[y] > 0),
                                        datasets: historicalData.crime_types.map((crimeType, index) => {
                                          const colors = [
                                            'rgba(239, 68, 68, 0.7)',   // Red
                                            'rgba(59, 130, 246, 0.7)',  // Blue
                                            'rgba(16, 185, 129, 0.7)',  // Green
                                            'rgba(245, 158, 11, 0.7)',  // Orange
                                            'rgba(139, 92, 246, 0.7)',  // Purple
                                            'rgba(236, 72, 153, 0.7)',  // Pink
                                            'rgba(20, 184, 166, 0.7)',  // Teal
                                            'rgba(251, 146, 60, 0.7)',  // Amber
                                            'rgba(168, 85, 247, 0.7)',  // Violet
                                            'rgba(14, 165, 233, 0.7)',  // Sky
                                            'rgba(34, 197, 94, 0.7)',   // Lime
                                            'rgba(249, 115, 22, 0.7)',  // Orange2
                                            'rgba(220, 38, 38, 0.7)',   // Red2
                                            'rgba(124, 58, 237, 0.7)'   // Indigo
                                          ];
                                          return {
                                            label: crimeType,
                                            data: Object.entries(historicalData.yearly_data)
                                              .filter(([y, d]) => d.total > 0)
                                              .map(([y, d]) => d.crime_breakdown?.[crimeType] || 0),
                                            backgroundColor: colors[index % colors.length],
                                            borderColor: colors[index % colors.length].replace('0.7', '1'),
                                            borderWidth: 1
                                          };
                                        })
                                      }}
                                      options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        scales: {
                                          y: {
                                            beginAtZero: true,
                                            stacked: true,
                                            title: {
                                              display: true,
                                              text: 'Number of Incidents'
                                            }
                                          },
                                          x: {
                                            stacked: true,
                                            title: {
                                              display: true,
                                              text: 'Year'
                                            }
                                          }
                                        },
                                        plugins: {
                                          legend: {
                                            display: true,
                                            position: 'top'
                                          },
                                          title: {
                                            display: true,
                                            text: 'Crime Types Breakdown by Year (Stacked)'
                                          }
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div className="flex items-center justify-center h-full text-gray-500">
                                      No crime type data available
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Detailed Year by Year Mode */}
                    {historicalViewMode === 'detailed' && (
                      <div className="space-y-4">
                        {Object.entries(historicalData.yearly_data || {})
                          .filter(([year, data]) => data.total > 0)
                          .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
                          .map(([year, data]) => (
                            <Card key={year}>
                              <CardHeader>
                                <CardTitle className="flex justify-between items-center">
                                  <span>Year {year}</span>
                                  <Badge variant="secondary">{data.total.toLocaleString()} Total Incidents</Badge>
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="text-center py-4">
                                  <div className="text-4xl font-bold text-blue-600 mb-2">
                                    {data.total.toLocaleString()}
                                  </div>
                                  <p className="text-gray-600">Total Part 1 Crimes</p>
                                </div>
                              </CardContent>
                            </Card>
                          ))
                        }
                      </div>
                    )}

                    {/* Decade Summary Mode */}
                    {historicalViewMode === 'decades' && (
                      <div className="space-y-6">
                        {showHistoricalCharts && (
                          <Card>
                            <CardHeader>
                              <CardTitle>Crime by Decade</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div style={{ height: '400px' }}>
                                <Bar
                                  data={{
                                    labels: Object.keys(historicalData.decade_summaries || {}),
                                    datasets: [
                                      {
                                        label: 'Total Incidents',
                                        data: Object.values(historicalData.decade_summaries || {}).map(d => d.total),
                                        backgroundColor: 'rgba(59, 130, 246, 0.6)',
                                        borderColor: 'rgba(59, 130, 246, 1)',
                                        borderWidth: 1
                                      }
                                    ]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: {
                                      y: {
                                        beginAtZero: true,
                                        title: {
                                          display: true,
                                          text: 'Number of Incidents'
                                        }
                                      },
                                      x: {
                                        title: {
                                          display: true,
                                          text: 'Decade'
                                        }
                                      }
                                    },
                                    plugins: {
                                      legend: {
                                        display: true,
                                        position: 'top'
                                      },
                                      title: {
                                        display: true,
                                        text: 'Crime Incidents by Decade'
                                      }
                                    }
                                  }}
                                />
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(historicalData.decade_summaries || {}).map(([decade, data]) => (
                            <Card key={decade}>
                              <CardHeader>
                                <CardTitle>{decade}</CardTitle>
                                <CardDescription>
                                  Years: {data.years_included.join(', ')}
                                </CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="text-center">
                                  <div className="text-4xl font-bold text-blue-600 mb-2">
                                    {data.total.toLocaleString()}
                                  </div>
                                  <p className="text-gray-600">Total Incidents</p>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <History className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Select a year range and click "Load Data" to view historical crime statistics</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Toaster />
    </div>
  );
}

export default App;